import type { FC } from "react";
import cx from "classnames";
import { Button } from "../Button";
import InfoIcon from "../../icons/info.svg?react";
import OrbitIcon from "../../icons/orbit.svg?react";
import LeftIcon from "../../icons/left.svg?react";
import RightIcon from "../../icons/right.svg?react";
import PlayIcon from "../../icons/play.svg?react";
import PauseIcon from "../../icons/pause.svg?react";

import styles from "./Suggestions.module.css";
import { AssistantSuggestion } from "../../types/assistant";
import { Sight, SightId } from "../../types";
import { useI18n } from "../../i18n";

type Props = {
  status: "idle" | "sending" | "awaiting" | "rendering" | "error";
  sights: Record<SightId, Sight>;
  suggestions: AssistantSuggestion[];
  handleSuggestion: (suggestion: AssistantSuggestion) => void;
};

/**
 * Inline suggestion list rendered at the end of the chat history.
 */
export const Suggestions: FC<Props> = ({
  status,
  sights,
  suggestions,
  handleSuggestion,
}) => {
  const i18n = useI18n();

  if (suggestions.length === 0) {
    return null;
  }

  const labelForSight = (sightId: SightId) => {
    const sight = sights[sightId];
    if (!sight) {
      return typeof sightId === "string" ? sightId : String(sightId);
    }
    return (
      sight.name ?? (typeof sight.id === "string" ? sight.id : String(sight.id))
    );
  };

  return (
    <div className={cx(styles.root, status === "idle" && styles.idle)}>
      <p className={styles.header}>{i18n.assistant.suggestions.header}</p>
      <ul className={styles.list}>
        {suggestions.map((suggestion, i) => (
          <li className={styles.item} key={i}>
            <Button
              className={styles.suggestionButton}
              onClick={() => handleSuggestion(suggestion)}
            >
              {(function () {
                switch (suggestion.type) {
                  case "orbit":
                    return (
                      <div className={styles.suggestionContent}>
                        <OrbitIcon />{" "}
                        <span>{suggestion.label || i18n.assistant.suggestions.orbit}</span>
                      </div>
                    );

                  case "info":
                    return (
                      <div className={styles.suggestionContent}>
                        <InfoIcon />{" "}
                        <span>{suggestion.label || i18n.assistant.suggestions.info}</span>
                      </div>
                    );

                  case "sight":
                    const sight = sights[suggestion.sightId];

                    if (!sight) {
                      return null;
                    }

                    return (
                      <div className={styles.suggestionContent}>
                        {sight.icon && (
                          <img
                            src={sight.icon.src}
                            srcSet={sight.icon.srcSet}
                            alt={sight.icon?.alt || sight.name}
                            width={20}
                            height={20}
                            className={styles.poiIcon}
                            loading="lazy"
                          />
                        )}
                        <span>{suggestion.label || labelForSight(suggestion.sightId)}</span>
                      </div>
                    );

                  case "more":
                    return suggestion.label || i18n.assistant.suggestions.moreFacts;

                  case "tour":
                    return suggestion.label || i18n.assistant.suggestions.startJourney;

                  case "tour-previous":
                    return (
                      <div className={styles.suggestionContent}>
                        <LeftIcon />{" "}
                        <span>{suggestion.label || i18n.assistant.suggestions.previous}</span>
                      </div>
                    );

                  case "tour-next":
                    return (
                      <div className={styles.suggestionContent}>
                        <RightIcon />{" "}
                        <span>{suggestion.label || i18n.assistant.suggestions.next}</span>
                      </div>
                    );

                  case "tour-continue":
                    return (
                      <div className={styles.suggestionContent}>
                        <PlayIcon />{" "}
                        <span>
                          {suggestion.label || i18n.assistant.suggestions.continueStory}
                        </span>
                      </div>
                    );

                  case "tour-stop":
                    return (
                      <div className={styles.suggestionContent}>
                        <PauseIcon />
                        <span>{suggestion.label || i18n.assistant.suggestions.pauseTour}</span>
                      </div>
                    );

                  default:
                    return null;
                }
              })()}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};
